/**
 * @zendir/ui - CesiumJS Capture Source
 *
 * Factory function that creates a headless (hidden DOM) CesiumJS viewer
 * for rendering satellite-viewpoint globe images. Designed to plug into
 * the Capture component's `imageSource` prop.
 *
 * The viewer is created once and reused across captures. Camera position,
 * orientation, FOV, and resolution are applied per-request from CaptureArgs.
 *
 * Requires `cesium` as an optional peer dependency. Tree-shakes away when
 * Cesium is not installed (dynamic import).
 *
 * @example
 * ```ts
 * import { createCesiumCaptureSource } from '@zendir/ui/react/3d';
 *
 * const source = await createCesiumCaptureSource({ accessToken: '...' });
 * <Capture imageSource={source.capture} />
 *
 * // Cleanup on unmount:
 * source.destroy();
 * ```
 */

import type { CaptureArgs } from "../core/widgets/Capture";
import { EARTH_RADIUS_KM, DEG_TO_RAD, RAD_TO_DEG } from "./ZenSpace3DUtils";

/** Lazy import avoids circular deps; returns bundled placeholder PNG on capture failure */
async function loadPlaceholderFallback(): Promise<Uint8Array> {
  const { loadCapturePlaceholderImage } =
    await import("../core/widgets/Capture");
  return loadCapturePlaceholderImage();
}

// ─── Types ────────────────────────────────────────────────────────────────────

export interface CesiumCaptureSourceOptions {
  /**
   * Container element to host the hidden Cesium viewer.
   * If not provided, one is auto-created and appended to document.body.
   */
  container?: HTMLElement;
  /** Cesium Ion default access token for imagery/terrain tiles */
  accessToken?: string;
  /**
   * Imagery provider URL template. Falls back to Cesium Ion default
   * when not specified and an accessToken is provided.
   */
  imageryUrl?: string;
  /** Enable terrain (requires Cesium Ion token). Default: false */
  terrain?: boolean;
  /** Maximum cached tile count (higher = more memory, fewer re-downloads). Default: 100 */
  maximumCachedTiles?: number;
  /**
   * Called during capture while waiting for tiles to load.
   * Receives elapsed milliseconds so the caller can show a progress indicator.
   */
  onTileLoadProgress?: (elapsedMs: number) => void;
  /** Max milliseconds to wait for tile loading per capture. Default: 10000 */
  tileLoadTimeoutMs?: number;
  /**
   * Use OpenStreetMap raster tiles (no Cesium Ion token required).
   * Strongly recommended for local dev and Storybook; default is true.
   * Set false to keep the Viewer default imagery (typically Cesium Ion / Bing — needs `accessToken`).
   */
  useOpenStreetMapImagery?: boolean;
}

export interface CesiumCaptureSourceHandle {
  /** Render a globe image from the given camera parameters. Pass as Capture's imageSource. */
  capture: (args: CaptureArgs) => Promise<Uint8Array>;
  /** Tear down the Cesium viewer, remove the hidden container, release WebGL context. */
  destroy: () => void;
  /** Whether the viewer is initialized and ready to render. */
  readonly ready: boolean;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

/**
 * Convert ECEF meters (Unreal / game-engine format) to SDK's ECI-like km format.
 *
 * ECEF (Earth-Centered Earth-Fixed): X→0°N 0°E, Y→0°N 90°E, Z→North Pole (meters)
 * SDK (ECI-like):                    X/Z→equatorial plane,   Y→North Pole  (km)
 *
 * Axis mapping:  SDK.x = ECEF.x / 1000
 *                SDK.y = ECEF.z / 1000   (ECEF Z=north → SDK Y=north)
 *                SDK.z = ECEF.y / 1000
 *
 * Export this so callers (e.g. MQTT handlers) can normalize at the boundary
 * before passing positions to CaptureArgs.
 */
export function ecefMetersToSdkPosition(
  ecef: [number, number, number],
): [number, number, number] {
  const [ex, ey, ez] = ecef;
  return [ex / 1000, ez / 1000, ey / 1000];
}

/**
 * Convert world-frame Euler XYZ rotation + ECEF position to Cesium HPR (degrees).
 *
 * The game engine sends the camera's absolute orientation as Euler 1-2-3
 * (XYZ intrinsic, right-handed, X=right Y=forward Z=up) in the ECEF world frame.
 * Cesium's heading/pitch/roll are relative to the local ENU (East-North-Up) frame
 * at the camera's geographic position, so we must:
 *
 *   1. Build the rotation matrix R from Euler XYZ (camera axes in ECEF)
 *   2. Extract the camera's forward (+Y) and up (+Z) vectors in ECEF
 *   3. Compute the ECEF→ENU rotation matrix at the camera's lat/lon
 *   4. Transform forward/up into the ENU frame
 *   5. Extract heading, pitch, roll from those ENU vectors
 *
 * @param ecefPos  Camera position in ECEF meters [x, y, z]
 * @param eulerDeg Euler XYZ rotation in degrees [rx, ry, rz]
 * @returns [heading, pitch, roll] in degrees (Cesium convention)
 */
export function ecefEulerXyzToHpr(
  ecefPos: [number, number, number],
  eulerDeg: [number, number, number],
): [number, number, number] {
  const [px, py, pz] = ecefPos;

  // ── Lat/Lon from ECEF position (geocentric, sufficient for orientation) ──
  const rHoriz = Math.sqrt(px * px + py * py);
  const lat = Math.atan2(pz, rHoriz);
  const lon = Math.atan2(py, px);
  const slat = Math.sin(lat),
    clat = Math.cos(lat);
  const slon = Math.sin(lon),
    clon = Math.cos(lon);

  // ── Build camera rotation matrix from Euler XYZ ──
  // R_game = Rz(rz) · Ry(ry) · Rx(rx)   (fixed-frame / extrinsic equivalent)
  // Columns of R_game are the camera's body axes expressed in ECEF.
  const rx = eulerDeg[0] * DEG_TO_RAD;
  const ry = eulerDeg[1] * DEG_TO_RAD;
  const rz = eulerDeg[2] * DEG_TO_RAD;
  const cx = Math.cos(rx),
    sx = Math.sin(rx);
  const cy = Math.cos(ry),
    sy = Math.sin(ry);
  const cz = Math.cos(rz),
    sz = Math.sin(rz);

  // Column 1 = camera forward (+Y body axis) in ECEF
  const fX = sx * sy * cz - cx * sz;
  const fY = sx * sy * sz + cx * cz;
  const fZ = sx * cy;

  // Column 2 = camera up (+Z body axis) in ECEF
  const uX = cx * sy * cz + sx * sz;
  const uY = cx * sy * sz - sx * cz;
  const uZ = cx * cy;

  // ── Transform forward and up from ECEF → ENU ──
  // R_enu rows: East  = [-slon,        clon,        0   ]
  //             North = [-slat*clon,   -slat*slon,   clat]
  //             Up    = [ clat*clon,    clat*slon,   slat]
  const fE = -slon * fX + clon * fY;
  const fN = -slat * clon * fX - slat * slon * fY + clat * fZ;
  const fU = clat * clon * fX + clat * slon * fY + slat * fZ;

  const uE = -slon * uX + clon * uY;
  const uN = -slat * clon * uX - slat * slon * uY + clat * uZ;
  const uU = clat * clon * uX + clat * slon * uY + slat * uZ;

  // ── Extract heading and pitch from forward vector in ENU ──
  const horizLen = Math.sqrt(fE * fE + fN * fN);
  // heading = CW from North: atan2(East component, North component)
  const heading = Math.atan2(fE, fN) * RAD_TO_DEG;
  // pitch = elevation from horizontal (negative = looking down)
  const pitch = Math.atan2(fU, horizLen) * RAD_TO_DEG;

  // ── Extract roll from up vector ──
  // Special case: looking straight up or down (gimbal lock for heading)
  if (horizLen < 1e-9) {
    const roll = Math.atan2(uE, uN) * RAD_TO_DEG;
    return [heading, pitch, roll];
  }

  // default_right = normalize( forward_enu × [0,0,1] ) — horizontal perp to forward
  // forward × Z_enu = [fE,fN,fU] × [0,0,1] = [fN, -fE, 0]
  const drLen = Math.sqrt(fN * fN + fE * fE); // = horizLen
  const drE = fN / drLen;
  const drN = -fE / drLen;
  // drU = 0

  // default_up = normalize( default_right × forward )
  // dr=[drE,drN,0], f=[fE,fN,fU]  (normalize f for a unit cross product)
  const fLen = Math.sqrt(fE * fE + fN * fN + fU * fU);
  const fnE = fE / fLen,
    fnN = fN / fLen,
    fnU = fU / fLen;
  const duE = drN * fnU; // drN*fnU - 0*fnN
  const duN = -drE * fnU; // 0*fnE - drE*fnU
  const duU = drE * fnN - drN * fnE;

  // roll = atan2( up·default_right, up·default_up )
  const roll =
    Math.atan2(
      uE * drE + uN * drN, // up · default_right (drU=0)
      uE * duE + uN * duN + uU * duU, // up · default_up
    ) * RAD_TO_DEG;

  return [heading, pitch, roll];
}

/**
 * Convert local-frame Euler XYZ intrinsic rotation (right-handed, degrees)
 * to Cesium heading/pitch/roll (degrees).
 *
 * Use this when the rotation is already relative to a local frame aligned with
 * ENU (e.g. manual test inputs). For world-frame ECEF rotations from Unreal,
 * use `ecefEulerXyzToHpr` instead — it accounts for the ENU frame at the
 * camera's geographic position.
 *
 * Euler XYZ: rotation[0] around X, [1] around Y, [2] around Z.
 * Decomposed into Cesium HPR via the rotation matrix R = Rz·Ry·Rx.
 */
export function eulerXyzToHpr(
  eulerDeg: [number, number, number],
): [number, number, number] {
  const rx = eulerDeg[0] * DEG_TO_RAD;
  const ry = eulerDeg[1] * DEG_TO_RAD;
  const rz = eulerDeg[2] * DEG_TO_RAD;

  const cx = Math.cos(rx),
    sx = Math.sin(rx);
  const cy = Math.cos(ry),
    sy = Math.sin(ry);
  const cz = Math.cos(rz),
    sz = Math.sin(rz);

  // R = Rz(rz) · Ry(ry) · Rx(rx)
  const R02 = cx * sy * cz + sx * sz;
  const R00 = cy * cz;
  const R01 = sx * sy * cz - cx * sz;
  const R12 = cx * sy * sz - sx * cz;
  const R22 = cx * cy;

  const heading = Math.atan2(R01, R00) * RAD_TO_DEG;
  const pitch = Math.asin(Math.max(-1, Math.min(1, -R02))) * RAD_TO_DEG;
  const roll = Math.atan2(-R12, R22) * RAD_TO_DEG;

  return [heading, pitch, roll];
}

/**
 * Convert CaptureArgs position [x, y, z] (km from Earth center, ECI-like)
 * to Cesium Cartesian3 (WGS84 meters).
 *
 * The Y axis points to the North Pole, X/Z define the equatorial plane
 * (matching the ZenSpace3D rendering convention after scale correction).
 *
 * Callers sending ECEF meter coordinates (e.g. Unreal Engine) should convert
 * with `ecefMetersToSdkPosition()` before passing to CaptureArgs.
 */
function positionToCartesian3(
  Cesium: any,
  position: [number, number, number],
): any {
  const [x, y, z] = position;
  const r = Math.sqrt(x * x + y * y + z * z);

  // Guard against zero-length position (would cause NaN in acos)
  if (r < 1) {
    return Cesium.Cartesian3.fromDegrees(0, 0, 400_000);
  }

  // Extract direction → lat/lon (independent of distance scale)
  const lat = 90 - Math.acos(Math.min(1, Math.max(-1, y / r))) * RAD_TO_DEG;
  const lon = Math.atan2(z, -x) * RAD_TO_DEG - 180;

  // Altitude is distance above Earth surface in km; Cesium needs meters
  const altKm = r - EARTH_RADIUS_KM;
  return Cesium.Cartesian3.fromDegrees(lon, lat, altKm * 1000);
}

/**
 * Compute the optical field of view from physical sensor parameters.
 *
 * Real satellite cameras derive FOV from:
 *   FOV = 2 × atan( (sensor_size / 2) / focal_length )
 * where sensor_size = resolution × pixel_pitch (mm).
 *
 * Returns the FOV in degrees, or null if the parameters are insufficient
 * (zero focal_length, zero pixel_pitch, or zero resolution).
 */
function computeOpticalFov(
  focalLengthMm: number,
  pixelPitchMm: number,
  resolutionPx: number,
): number | null {
  if (focalLengthMm <= 0 || pixelPitchMm <= 0 || resolutionPx <= 0) return null;
  const sensorSizeMm = resolutionPx * pixelPitchMm;
  return 2 * Math.atan(sensorSizeMm / (2 * focalLengthMm)) * RAD_TO_DEG;
}

/**
 * Apply grayscale filter to canvas pixel data in-place.
 * Uses luminance weights (ITU-R BT.709).
 */
function applyGrayscale(
  ctx: CanvasRenderingContext2D,
  width: number,
  height: number,
): void {
  const imageData = ctx.getImageData(0, 0, width, height);
  const data = imageData.data;
  for (let i = 0; i < data.length; i += 4) {
    const lum = 0.2126 * data[i] + 0.7152 * data[i + 1] + 0.0722 * data[i + 2];
    data[i] = data[i + 1] = data[i + 2] = lum;
  }
  ctx.putImageData(imageData, 0, 0);
}

// ─── Depth-of-Field Simulation ────────────────────────────────────────────────

/**
 * Estimate the nadir subject distance in meters from the camera position.
 *
 * The SDK position is in ECI-like coordinates (km). Camera altitude above
 * the Earth surface gives the approximate distance to the ground plane
 * directly below (nadir). For oblique views the actual distance is larger,
 * but nadir is the dominant case for satellite imagery.
 */
function estimateSubjectDistanceM(position: [number, number, number]): number {
  const [x, y, z] = position;
  const rKm = Math.sqrt(x * x + y * y + z * z);
  const altKm = Math.max(0, rKm - EARTH_RADIUS_KM);
  return altKm * 1000;
}

/**
 * Compute depth-of-field blur radius in CSS pixels using the thin lens model.
 *
 * Thin lens circle of confusion (CoC) diameter:
 *   c = |f²/N × (S - D) / (D × (S - f))|
 *
 * where:
 *   f = focal length (mm)
 *   N = f-number (aperture)
 *   S = focusing distance (mm) — the plane in sharp focus
 *   D = subject distance (mm) — actual distance to the scene
 *
 * The CoC diameter is converted to pixels via pixel_pitch, then halved to
 * get a blur radius. The result is clamped by the sensor's maximum
 * acceptable CoC (args.coc) to model physical diffraction limits.
 *
 * Returns 0 when the scene is in acceptable focus (blur < 0.5px).
 * Exported for unit testing.
 */
export function computeDofBlurPx(
  args: CaptureArgs,
  subjectDistanceM: number,
): number {
  const fMm = args.focal_length;
  const N = args.aperture;
  const sMm = args.focusing_distance * 1000; // meters → mm
  const dMm = subjectDistanceM * 1000; // meters → mm
  const ppMm = args.pixel_pitch;

  if (N <= 0 || fMm <= 0 || dMm <= 0 || ppMm <= 0 || sMm <= fMm) return 0;

  // Subject is essentially at the focus plane — no blur needed
  if (Math.abs(sMm - dMm) < 1) return 0;

  // Thin lens CoC diameter in mm
  // c = |f²/N × (S - D) / (D × (S - f))|
  const cocDiameterMm = Math.abs(
    (((fMm * fMm) / N) * (sMm - dMm)) / (dMm * (sMm - fMm)),
  );

  // Note: args.coc is the sensor's acceptable-focus criterion (not a render clamp).
  // We render the FULL optical blur — the sensor CoC only defines what humans
  // consider "in focus" for DoF range calculations, not the actual blur disc.

  // Convert diameter → pixel radius
  const radiusPx = cocDiameterMm / ppMm / 2;

  // Below half a pixel is imperceptible; cap at 1/4 of image to prevent whiteout
  if (radiusPx < 0.5) return 0;
  return Math.min(radiusPx, args.resolution / 4);
}

/**
 * Apply Gaussian depth-of-field blur to a 2D canvas context.
 * Uses the native CSS `filter: blur()` for GPU-accelerated performance.
 *
 * The blur is applied by:
 *  1. Drawing the current canvas content onto a temp canvas with blur filter
 *  2. Copying the blurred result back
 *
 * This simulates the optical defocus that occurs when the subject distance
 * doesn't match the focusing distance — exactly what happens when a
 * satellite camera's focus plane is set incorrectly.
 */
function applyDofBlur(
  ctx: CanvasRenderingContext2D,
  width: number,
  height: number,
  radiusPx: number,
): void {
  const tmp = document.createElement("canvas");
  tmp.width = width;
  tmp.height = height;
  const tmpCtx = tmp.getContext("2d")!;

  // CSS filter blur uses Gaussian sigma ≈ radius; the visual result closely
  // matches a real optical defocus disc for moderate blur amounts.
  tmpCtx.filter = `blur(${radiusPx}px)`;
  tmpCtx.drawImage(ctx.canvas, 0, 0);

  // Copy blurred result back, replacing the original sharp image
  ctx.clearRect(0, 0, width, height);
  ctx.drawImage(tmp, 0, 0);
}

/**
 * Convert a data-URL string to a Uint8Array of the underlying binary data.
 */
function dataUrlToBytes(dataUrl: string): Uint8Array {
  const base64 = dataUrl.split(",")[1];
  const binaryStr = atob(base64);
  const bytes = new Uint8Array(binaryStr.length);
  for (let i = 0; i < binaryStr.length; i++) {
    bytes[i] = binaryStr.charCodeAt(i);
  }
  return bytes;
}

// ─── Factory ──────────────────────────────────────────────────────────────────

/**
 * Creates a reusable CesiumJS capture source backed by a hidden viewer.
 *
 * The viewer is initialized once with `preserveDrawingBuffer: true` so
 * canvas pixels survive after `scene.render()`. All Cesium UI widgets
 * are disabled. The container is positioned off-screen (1x1 px) and
 * resized to the requested resolution on each capture.
 *
 * @param options - Configuration for the hidden viewer
 * @returns Handle with `capture(args)`, `destroy()`, and `ready` flag
 */
export async function createCesiumCaptureSource(
  options: CesiumCaptureSourceOptions = {},
): Promise<CesiumCaptureSourceHandle> {
  // Cesium is untyped when loaded via dynamic import in some TS configs
  const Cesium: any = await import("cesium");

  // Set default access token if provided
  if (options.accessToken) {
    Cesium.Ion.defaultAccessToken = options.accessToken;
  }

  // Create or reuse a hidden container.
  // CRITICAL: The container must remain fully GPU-rendered for WebGL readback.
  //   - `left:-9999px`  → GPU skips rendering (off-viewport optimisation)
  //   - `visibility:hidden` → some browsers throttle WebGL compositing
  //   - `display:none` → element has no layout, canvas is 0x0
  // Solution: keep it on-screen at 0,0 but use `clip-path: inset(100%)` to
  // hide it visually. The element participates in layout AND the GPU renders
  // the WebGL context, but nothing is painted to the screen.
  let container = options.container ?? null;
  let ownsContainer = false;
  if (!container) {
    container = document.createElement("div");
    container.style.cssText =
      "position:fixed;left:0;top:0;width:1px;height:1px;overflow:hidden;" +
      "clip-path:inset(100%);pointer-events:none;z-index:-1;";
    document.body.appendChild(container);
    ownsContainer = true;
  }

  // Create a minimal Cesium Viewer with no UI chrome
  const viewer: any = new Cesium.Viewer(container, {
    baseLayerPicker: false,
    fullscreenButton: false,
    vrButton: false,
    geocoder: false,
    homeButton: false,
    sceneModePicker: false,
    timeline: false,
    navigationHelpButton: false,
    animation: false,
    scene3DOnly: true,
    // Only render when we explicitly call scene.render()
    useDefaultRenderLoop: false,
    requestRenderMode: true,
    contextOptions: {
      webgl: { preserveDrawingBuffer: true },
    },
  });

  // Globe settings for clean captures:
  // - Disable dynamic lighting so the globe is fully illuminated regardless of time-of-day
  // - Remove skybox/moon chrome for clean satellite imagery output
  viewer.scene.globe.enableLighting = false;
  viewer.scene.backgroundColor = Cesium.Color.BLACK;
  viewer.scene.skyBox = undefined as any;
  viewer.scene.moon = undefined as any;

  // Raster imagery that does not require a Cesium Ion token (Storybook / local dev).
  // Default Ion/Bing layers often fail silently without a token → black frames.
  // We try multiple OSM-compatible tile sources for reliability.
  const useOsm = options.useOpenStreetMapImagery !== false;
  if (useOsm) {
    viewer.imageryLayers.removeAll();

    // ESRI World Imagery — real satellite/aerial photography. No token required
    // for reasonable usage. Far more realistic than Carto basemaps for a
    // satellite-camera simulator (shows actual land, ocean, clouds).
    // Falls back to Carto Voyager (visible colors) if ESRI is unreachable.
    const tileUrl =
      options.imageryUrl ||
      "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}";

    viewer.imageryLayers.addImageryProvider(
      new Cesium.UrlTemplateImageryProvider({
        url: tileUrl,
        credit: "© Esri, Maxar, Earthstar Geographics",
        maximumLevel: 19,
      }),
    );
  }

  // Enable terrain if requested
  if (options.terrain && options.accessToken) {
    try {
      viewer.scene.terrainProvider =
        await Cesium.CesiumTerrainProvider.fromIonAssetId(1);
    } catch {
      // Fall back to ellipsoid terrain silently
    }
  }

  let destroyed = false;

  const capture = async (args: CaptureArgs): Promise<Uint8Array> => {
    if (destroyed) return new Uint8Array(0);

    try {
      // Determine render resolution (halve for sample/preview mode)
      const res = args.sample
        ? Math.max(64, Math.round(args.resolution / 2))
        : args.resolution;

      // Resize the container to the requested resolution.
      // The sequence is critical for correct WebGL framebuffer dimensions:
      //   1. Set CSS dimensions on the container
      //   2. Force a synchronous reflow so clientWidth/clientHeight update
      //   3. Call viewer.resize() — Cesium reads clientWidth/clientHeight
      //   4. Yield an rAF so the GPU/GL viewport picks up the new canvas size
      // Getting this order wrong causes Cesium to render at stale dimensions
      // (e.g. 1×1) and produce distorted/stretched imagery.
      container!.style.width = `${res}px`;
      container!.style.height = `${res}px`;

      // Force synchronous reflow — reading a layout property forces the browser
      // to recalculate layout immediately, ensuring clientWidth/clientHeight
      // reflect the new CSS dimensions before Cesium reads them.
      void container!.clientWidth;

      viewer.resize();

      // Yield two animation frames: the first lets the browser commit the layout
      // change to the compositor; the second ensures the GL viewport/framebuffer
      // is fully allocated at the new dimensions before we render.
      await new Promise<void>((r) => requestAnimationFrame(() => r()));
      await new Promise<void>((r) => requestAnimationFrame(() => r()));

      // Resize again after frames — catches edge cases where the canvas element
      // inside the viewer didn't update dimensions during the first resize.
      viewer.resize();

      // Position the camera using CaptureArgs scene coordinates
      const cameraPosition = positionToCartesian3(Cesium, args.position);
      const [heading, pitch, roll] = args.rotation;

      viewer.camera.setView({
        destination: cameraPosition,
        orientation: {
          heading: Cesium.Math.toRadians(heading),
          pitch: Cesium.Math.toRadians(pitch),
          roll: Cesium.Math.toRadians(roll),
        },
      });

      // Resolve effective FOV — prefer the explicit fov arg, but fall back to
      // the optically-computed value from focal_length + pixel_pitch + resolution
      // when fov is invalid (≤0 or ≥180). This ensures realistic captures even
      // when the caller only provides physical optics parameters.
      const opticalFov = computeOpticalFov(
        args.focal_length,
        args.pixel_pitch,
        args.resolution,
      );
      let effectiveFov = args.fov;

      if (effectiveFov <= 0 || effectiveFov >= 180) {
        // Explicit FOV is invalid — use the optics-derived value if available
        if (opticalFov !== null && opticalFov > 0 && opticalFov < 180) {
          effectiveFov = opticalFov;
          if (import.meta.env.DEV) {
            console.debug(
              `[CesiumCapture] fov=${args.fov}° invalid, using optics-derived FOV ` +
                `= ${opticalFov.toFixed(2)}° (f=${args.focal_length}mm, pitch=${args.pixel_pitch}mm, ` +
                `res=${args.resolution}px)`,
            );
          }
        }
      } else if (import.meta.env.DEV && opticalFov !== null) {
        // Both are valid — warn when they disagree significantly (>5% relative error).
        // This catches configuration mistakes where the optics don't match the stated FOV.
        const relError = Math.abs(effectiveFov - opticalFov) / opticalFov;
        if (relError > 0.05) {
          console.warn(
            `[CesiumCapture] Explicit fov=${effectiveFov}° differs from optics-derived ` +
              `FOV=${opticalFov.toFixed(2)}° by ${(relError * 100).toFixed(1)}%. ` +
              `Check focal_length=${args.focal_length}mm / pixel_pitch=${args.pixel_pitch}mm.`,
          );
        }
      }

      if (effectiveFov > 0 && effectiveFov < 180) {
        (viewer.camera.frustum as any).fov = effectiveFov * DEG_TO_RAD;
      }

      // Render in a loop until imagery tiles finish loading.
      // Cesium streams tiles over the network, so the first render is often blank.
      // We poll globe.tilesLoaded (true when all visible tiles are resident),
      // up to a timeout to avoid hanging on slow networks.
      const maxWaitMs = options.tileLoadTimeoutMs ?? 10_000;
      const pollIntervalMs = 200;
      const startMs = performance.now();
      const deadline = startMs + maxWaitMs;

      // Initial render — forces tile requests to be dispatched
      viewer.scene.requestRender();
      viewer.scene.forceRender();

      // Poll until tiles are loaded or timeout. A first render can trigger tile
      // downloads; subsequent forceRender() calls rasterize newly arrived tiles.
      let tileWaitLoops = 0;
      while (!viewer.scene.globe.tilesLoaded && performance.now() < deadline) {
        tileWaitLoops++;
        options.onTileLoadProgress?.(performance.now() - startMs);
        await new Promise<void>((r) => setTimeout(r, pollIntervalMs));
        viewer.scene.requestRender();
        viewer.scene.forceRender();
      }

      // Three more forced frames after tiles report ready:
      // 1st: GPU uploads decoded texture data from newly arrived tiles
      // 2nd: composites all layers into the draw buffer
      // 3rd: ensures the preserveDrawingBuffer content is stable for readback
      viewer.scene.requestRender();
      viewer.scene.forceRender();
      await new Promise<void>((r) => requestAnimationFrame(() => r()));
      viewer.scene.requestRender();
      viewer.scene.forceRender();
      await new Promise<void>((r) => requestAnimationFrame(() => r()));
      viewer.scene.forceRender();

      const canvas = viewer.scene.canvas as HTMLCanvasElement;

      if (import.meta.env.DEV) {
        const waitMs = Math.round(performance.now() - startMs);
        const tilesOk = viewer.scene.globe.tilesLoaded;
        console.debug(
          `[CesiumCapture] ${res}x${res} canvas=${canvas.width}x${canvas.height} ` +
            `tiles=${tilesOk ? "ok" : "TIMEOUT"} loops=${tileWaitLoops} wait=${waitMs}ms`,
        );
      }

      // Verify the canvas actually has non-black content (dev-only diagnostic).
      // Guard both getContext result and readPixels existence — Cesium's canvas
      // may expose webgl/webgl2 or neither depending on context options.
      if (import.meta.env.DEV && canvas.width > 0 && canvas.height > 0) {
        try {
          const sampleCtx =
            canvas.getContext("webgl2") || canvas.getContext("webgl");
          if (
            sampleCtx &&
            typeof (sampleCtx as any).readPixels === "function"
          ) {
            const gl = sampleCtx as WebGLRenderingContext;
            const px = new Uint8Array(4);
            gl.readPixels(
              Math.floor(canvas.width / 2),
              Math.floor(canvas.height / 2),
              1,
              1,
              gl.RGBA,
              gl.UNSIGNED_BYTE,
              px,
            );
            if (px[0] === 0 && px[1] === 0 && px[2] === 0) {
              console.warn(
                `[CesiumCapture] Center pixel is black — tiles may not have loaded. ` +
                  `Check network for OSM tile 403/CORS errors.`,
              );
            }
          }
        } catch {
          // Non-critical diagnostic — don't let it crash the capture pipeline
        }
      }

      // Resolve output format — default to PNG when omitted
      const fmt = args.format === "jpeg" ? "image/jpeg" : "image/png";
      const jpegQuality =
        args.format === "jpeg" ? (args.jpeg_quality ?? 0.92) : undefined;

      // ── Post-processing pipeline ──────────────────────────────────────
      // Order matters: DoF blur → monochromatic → encode.
      // All post-processing happens on an offscreen 2D canvas to avoid
      // double-reading the WebGL draw buffer.

      // Compute DoF blur eagerly so we know whether an offscreen pass is needed
      let dofBlurRadius = 0;
      if (args.focusing_distance > 0 && args.aperture > 0) {
        const subjectDist = estimateSubjectDistanceM(args.position);
        dofBlurRadius = computeDofBlurPx(args, subjectDist);
      }

      const needsOffscreen = dofBlurRadius > 0 || args.monochromatic;

      if (needsOffscreen) {
        const offscreen = document.createElement("canvas");
        offscreen.width = canvas.width;
        offscreen.height = canvas.height;
        const ctx = offscreen.getContext("2d")!;
        ctx.drawImage(canvas, 0, 0);

        // 1. Depth-of-field blur: simulate optical defocus from the thin lens model
        if (dofBlurRadius > 0) {
          applyDofBlur(ctx, offscreen.width, offscreen.height, dofBlurRadius);
          if (import.meta.env.DEV) {
            const subjectDist = estimateSubjectDistanceM(args.position);
            console.debug(
              `[CesiumCapture] DoF blur: radius=${dofBlurRadius.toFixed(1)}px ` +
                `(subject=${(subjectDist / 1000).toFixed(0)}km, focus=${args.focusing_distance}m, ` +
                `f/${args.aperture}, f=${args.focal_length}mm)`,
            );
          }
        }

        // 2. Monochromatic: BT.709 luminance grayscale
        if (args.monochromatic) {
          applyGrayscale(ctx, offscreen.width, offscreen.height);
        }

        const dataUrl =
          jpegQuality !== undefined
            ? offscreen.toDataURL(fmt, jpegQuality)
            : offscreen.toDataURL(fmt);
        return dataUrlToBytes(dataUrl);
      }

      const dataUrl =
        jpegQuality !== undefined
          ? canvas.toDataURL(fmt, jpegQuality)
          : canvas.toDataURL(fmt);
      return dataUrlToBytes(dataUrl);
    } catch (err) {
      // Log the actual error in dev so we can diagnose rendering failures
      if (import.meta.env.DEV) {
        console.error("[CesiumCapture] Capture failed:", err);
      }
      return loadPlaceholderFallback();
    }
  };

  const destroy = () => {
    if (destroyed) return;
    destroyed = true;
    try {
      viewer.destroy();
    } catch {
      // Viewer may already be destroyed
    }
    if (ownsContainer && container && container.parentNode) {
      container.parentNode.removeChild(container);
    }
  };

  return {
    capture,
    destroy,
    get ready() {
      return !destroyed;
    },
  };
}
